import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Camera, X } from 'lucide-react';

const inputCls = 'bg-slate-800 border-slate-700 text-white text-sm h-10';
const MODES = [
  { key: 'xrp', label: 'XRP only' },
  { key: 'fuzzy', label: 'FUZZY only' },
  { key: 'both', label: "Buyer's choice ⇄" },
];

export default function SellCardDialog({ open, onOpenChange, sellerFeePct, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', grade: '', condition: '', cert_number: '',
    payment_mode: 'xrp', price_xrp: '', price_fuzzy: '', quantity: '1', allow_offers: true,
  });
  const [photoUrls, setPhotoUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photoUrls.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API}/upload/photo`, fd);
        const url = r.data?.url || r.data?.photo_url;
        if (url) setPhotoUrls(p => [...p, url]);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Photo upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Give your card a title'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/shop/listings`, {
        ...form,
        photo_urls: photoUrls,
        price_xrp: parseFloat(form.price_xrp) || 0,
        price_fuzzy: parseFloat(form.price_fuzzy) || 0,
        quantity: Math.max(1, parseInt(form.quantity) || 1),
      });
      toast.success(res.data?.message || 'Listing submitted!');
      setForm({ title: '', description: '', grade: '', condition: '', cert_number: '', payment_mode: 'xrp', price_xrp: '', price_fuzzy: '', quantity: '1', allow_offers: true });
      setPhotoUrls([]);
      onOpenChange(false);
      onCreated?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not submit listing'); }
    finally { setBusy(false); }
  };

  const mode = form.payment_mode;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sell-card-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg text-white">Sell a Card</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Approved hosts go live instantly (others after admin review) · platform takes {sellerFeePct}% of the sale · you ship, escrow protects both sides
          </DialogDescription>
        </DialogHeader>
        <p className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
          📦 Physical items MUST ship signature-required. If you ship without a signature requirement, the sale is final and you carry the non-delivery risk.
        </p>
        <div className="space-y-2.5">
          <Input data-testid="sell-title" placeholder="Card title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} />
          <Input placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Grade (PSA 9…)" value={form.grade} onChange={e => setForm(f => ({ ...f, grade: e.target.value }))} className={inputCls} />
            <Input placeholder="Condition" value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Cert # (optional)" value={form.cert_number} onChange={e => setForm(f => ({ ...f, cert_number: e.target.value }))} className={inputCls} />
            <Input data-testid="sell-quantity" type="number" placeholder="Quantity" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className={inputCls} />
          </div>

          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Photos ({photoUrls.length}/5)</p>
            <div className="flex gap-2 flex-wrap">
              {photoUrls.map((u, i) => (
                <div key={i} className="relative">
                  <img src={u} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-700" />
                  <button onClick={() => setPhotoUrls(p => p.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center"><X className="w-2.5 h-2.5 text-white" /></button>
                </div>
              ))}
              {photoUrls.length < 5 && (
                <label className="w-14 h-14 rounded-lg border border-dashed border-slate-600 flex items-center justify-center cursor-pointer bg-slate-800/50">
                  <Camera className="w-4 h-4 text-slate-500" />
                  <input data-testid="sell-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} disabled={uploading} />
                </label>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 uppercase">Payment</p>
            <div className="grid grid-cols-3 gap-1.5">
              {MODES.map(m => (
                <button key={m.key} data-testid={`sell-mode-${m.key}`} onClick={() => setForm(f => ({ ...f, payment_mode: m.key }))}
                  className={`rounded-lg border px-1 py-2 text-[10px] font-bold ${mode === m.key ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-300' : 'bg-slate-800/50 border-slate-700 text-slate-400'}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {mode !== 'fuzzy' && <Input data-testid="sell-price-xrp" type="number" placeholder="Price XRP *" value={form.price_xrp} onChange={e => setForm(f => ({ ...f, price_xrp: e.target.value }))} className={inputCls} />}
              {mode !== 'xrp' && <Input data-testid="sell-price-fuzzy" type="number" placeholder="Price FUZZY *" value={form.price_fuzzy} onChange={e => setForm(f => ({ ...f, price_fuzzy: e.target.value }))} className={inputCls} />}
            </div>
            {mode === 'both' && <p className="text-[10px] text-slate-500">Buyer flips the switch and pays in either currency.</p>}
          </div>

          <button
            type="button"
            data-testid="sell-allow-offers"
            onClick={() => setForm(f => ({ ...f, allow_offers: !f.allow_offers }))}
            className={`w-full flex items-center justify-between rounded-lg border p-2.5 text-xs font-bold ${form.allow_offers ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300' : 'bg-slate-800/50 border-slate-700 text-slate-400'}`}
          >
            {form.allow_offers ? 'Offers welcome — buyers can negotiate' : 'Set price only — no offers'}
            <span className={`w-9 h-4.5 rounded-full relative ${form.allow_offers ? 'bg-emerald-500' : 'bg-slate-700'}`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${form.allow_offers ? 'left-[20px]' : 'left-0.5'}`} />
            </span>
          </button>

          <Button data-testid="sell-submit" onClick={submit} disabled={busy || uploading} className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 h-11 font-semibold">
            {busy ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
